import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import FormField from "./FormField";
import { cn, colors } from "tools/ui";
import { theme } from "theme/theme";

const styleBase =
  "min-h-20 w-full py-2 px-2 outline-none whitespace-pre-wrap break-words " +
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 " +
  "empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400";
const styleButton =
  "h-7 min-w-7 px-2 rounded-md text-sm text-slate-700 hover:bg-slate-200";
const BLOCK_TAGS = ["DIV", "P", "UL", "OL", "LI"];

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineToHtml(text) {
  return escapeHtml(text).replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
}

export function textToHtml(text) {
  if (!text) return "";
  const html = [];
  let list = null;

  function closeList() {
    if (list) {
      html.push(`</${list}>`);
      list = null;
    }
  }

  for (const line of text.split("\n")) {
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    const item = bullet ?? numbered;
    if (item) {
      const tag = bullet ? "ul" : "ol";
      if (list !== tag) {
        closeList();
        html.push(`<${tag}>`);
        list = tag;
      }
      html.push(`<li>${inlineToHtml(item[1]) || "<br>"}</li>`);
    } else {
      closeList();
      html.push(`<div>${inlineToHtml(line) || "<br>"}</div>`);
    }
  }
  closeList();
  return html.join("");
}

function isBold(node) {
  if (["B", "STRONG"].includes(node.nodeName)) return true;
  const weight = node.style?.fontWeight;
  return weight === "bold" || Number(weight) >= 600;
}

function inlineToText(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.nodeValue.replace(/ /g, " ");
  }
  if (node.nodeName === "BR") return " ";
  let text = "";
  for (const child of node.childNodes) {
    text += inlineToText(child);
  }
  if (isBold(node) && text.trim()) {
    const [, start, inner, end] = text.match(/^(\s*)([\s\S]*?)(\s*)$/);
    return `${start}**${inner}**${end}`;
  }
  return text;
}

function blockToLines(node) {
  const lines = [];
  let current = "";
  let dirty = false;

  function flush() {
    if (dirty) lines.push(current);
    current = "";
    dirty = false;
  }

  for (const child of node.childNodes) {
    const tag = child.nodeName;
    if (tag === "BR") {
      lines.push(current);
      current = "";
      dirty = false;
    } else if (tag === "UL" || tag === "OL") {
      flush();
      let num = 1;
      for (const li of child.children) {
        const prefix = tag === "UL" ? "- " : `${num++}. `;
        lines.push(prefix + inlineToText(li).trim());
      }
    } else if (BLOCK_TAGS.includes(tag)) {
      flush();
      const childLines = blockToLines(child);
      lines.push(...(childLines.length ? childLines : [""]));
    } else {
      current += inlineToText(child);
      dirty = true;
    }
  }
  flush();
  return lines;
}

export function htmlToText(root) {
  return blockToLines(root).join("\n").replace(/\s+$/, "");
}

function RichText(props) {
  const {
    name,
    data,
    field,
    label,
    readOnly,
    placeholder,
    onChange,
    colSpan,
    style,
    model,
  } = props;
  const intl = useIntl();
  const editorRef = useRef(null);
  const lastValue = useRef(null);
  const [active, setActive] = useState({});
  const _value = data.value || "";

  useEffect(() => {
    if (!editorRef.current || _value === lastValue.current) return;
    editorRef.current.innerHTML = textToHtml(_value);
    lastValue.current = _value;
  }, [_value]);

  let _color = props.color ?? "bg-white";
  if (readOnly) {
    _color = colors.readOnly;
  }

  function updateActive() {
    setActive({
      bold: document.queryCommandState("bold"),
      ul: document.queryCommandState("insertUnorderedList"),
      ol: document.queryCommandState("insertOrderedList"),
    });
  }

  function emitChange() {
    const text = htmlToText(editorRef.current);
    if (text === lastValue.current) return;
    lastValue.current = text;
    data.value = text;
    if (onChange) {
      onChange(name, text);
    }
  }

  function handleCommand(event, command) {
    event.preventDefault();
    editorRef.current.focus();
    document.execCommand(command);
    emitChange();
    updateActive();
  }

  function handlePaste(event) {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }

  function handleFocus() {
    document.execCommand("defaultParagraphSeparator", false, "div");
    updateActive();
  }

  const buttons = [
    { command: "bold", key: "bold", text: "B", className: "font-bold" },
    { command: "insertUnorderedList", key: "ul", text: "•" },
    { command: "insertOrderedList", key: "ol", text: "1." },
  ];

  return (
    <FormField
      label={label}
      name={name}
      required={field?.required}
      colspan={colSpan}
      model={model}
    >
      <div
        className={cn(
          "w-full rounded-md overflow-hidden",
          _color,
          theme.border,
          style,
        )}
      >
        {!readOnly && (
          <div className="flex gap-1 px-1 py-1 border-b border-slate-200 bg-slate-50">
            {buttons.map((btn) => (
              <button
                key={btn.key}
                type="button"
                title={intl.formatMessage({ id: `rich_text.${btn.key}` })}
                className={cn(
                  styleButton,
                  btn.className,
                  active[btn.key] && "bg-slate-300",
                )}
                onMouseDown={(e) => handleCommand(e, btn.command)}
              >
                {btn.text}
              </button>
            ))}
          </div>
        )}
        <div
          ref={editorRef}
          id={name}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          className={cn(styleBase, theme.textColor)}
          data-placeholder={placeholder ?? ""}
          onInput={emitChange}
          onBlur={emitChange}
          onPaste={handlePaste}
          onFocus={handleFocus}
          onKeyUp={updateActive}
          onMouseUp={updateActive}
        />
      </div>
    </FormField>
  );
}

export default RichText;
